// React Components and Hooks
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import moment from "moment";
import { Box, Typography, Skeleton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { makeStyles } from "@mui/styles";

// Custom application component imports
import { LeftSidebar } from "layout-blueprints";
import { getAllPropertyPreviews } from "../../modules/apl/api/actions";
import {
  DashboardPeriodProvider,
  DASHBOARD_PERIOD_LAST30,
  DASHBOARD_PERIOD_MTD,
  useDashboardPeriod,
} from "./dashboardPeriodContext";
import DisconnectPeriodMetricsCard from "./DisconnectPeriodMetricsCard";
import {
  fetchDashboardResource,
  getDashboardErrorMessage,
  isDashboardAuthPending,
} from "./dashboardFetch";
import { logout } from "../../modules/user/api/actions";
import { formatMoney, numberWithCommas } from "../../setup/helpers";
import { formatWorkOrderAddress } from "../../modules/workOrders/utils/formatWorkOrderAddress";
import { complaintRowDueYmd } from "../../modules/complaints/utils/complaintResponseDue";
import vars from "../../assets/core/variables-mui";

/**
 * 0→1 eased progress for mount / data-change entrance animations.
 * Re-animates when dependency values change.
 */
function useEntranceProgress(deps, durationMs = 700) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    setProgress(0);
    const start = performance.now();
    let rafId;
    const easeOutCubic = (t) => 1 - (1 - t) ** 3;
    const tick = (now) => {
      const raw = Math.min(1, (now - start) / durationMs);
      setProgress(easeOutCubic(raw));
      if (raw < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, deps);
  return progress;
}

/** Past due total: GET /get-accounts-filtered/?page=0&pageLimit=0&status=Active — summary.combinedPastDue (SUM AccountBalance.pastDue). Omit minimumAccountObject so the full-preview path runs (BaseItem summary has no combinedPastDue). Not the same as GET /accountbills-pastdue (billing rows for batch ops). */

// App-aligned palette (see src/theme/index.js + assets/core/variables-mui.js)
const dash = {
  bg: vars.grey50,
  panel: "#ffffff",
  panelBorder: vars.grey300,
  text: vars.grey900,
  muted: vars.grey600,
  accent: vars.primary,
  accentLight: vars.primaryLight,
  accentDark: vars.primaryDark,
  accentTrack: vars.grey200,
  /** Gold highlight for on-target bars, gauge, band (secondary scale — no green) */
  chartHighlightLight: vars.secondaryLight,
  chartHighlightDark: vars.secondaryDark,
  chartBand: "rgba(255, 215, 0, 0.16)",
  risk: vars.error,
  yellow: vars.secondary,
  yellowMuted: vars.secondaryDark,
  gridLine: "rgba(0, 0, 0, 0.08)",
};

/** Prevent currency figures from overflowing narrow dashboard cards. */
const dashboardCurrencyAmountSx = {
  fontWeight: 700,
  lineHeight: 1.15,
  fontVariantNumeric: "tabular-nums",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  fontSize: "clamp(0.875rem, 1.5vw + 0.75rem, 1.25rem)",
};

const dashboardLargeCurrencyAmountSx = {
  ...dashboardCurrencyAmountSx,
  fontSize: "clamp(1rem, 2.5vw + 0.75rem, 2rem)",
};

const dashboardMetricStatBoxSx = {
  flex: "1 1 140px",
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  boxSizing: "border-box",
};

const DASHBOARD_DUE_LIST_LIMIT = 10;
const DASHBOARD_DUE_TAB_WORK_ORDERS = "workOrders";
const DASHBOARD_DUE_TAB_COMPLAINTS = "complaints";

const dashboardDueTabToggleSx = {
  flexShrink: 0,
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.75rem",
    px: 1.25,
    py: 0.35,
    lineHeight: 1.3,
  },
};
/** Header + app content padding (see layout-components/Header/_variables.scss). */
const DASHBOARD_VIEWPORT_OFFSET = 118;

/** Scrollable list body — fills remaining card height, does not expand the dashboard. */
const dashboardListScrollSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0, 0, 0, 0.22) transparent",
  "&::-webkit-scrollbar": {
    width: 5,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
    marginBlock: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.16)",
    borderRadius: 999,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.28)",
  },
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: `calc(100vh - ${DASHBOARD_VIEWPORT_OFFSET}px)`,
    maxHeight: `calc(100vh - ${DASHBOARD_VIEWPORT_OFFSET}px)`,
    minWidth: 0,
    overflow: "hidden",
    backgroundColor: dash.bg,
    color: dash.text,
    padding: "12px 14px 16px",
    boxSizing: "border-box",
    borderRadius: 10,
    marginTop: -20,
  },
  /** Match WeekView day cells: lift + shadow on hover */
  card: {
    position: "relative",
    zIndex: 0,
    backgroundColor: dash.panel,
    borderRadius: 10,
    border: `1px solid ${dash.panelBorder}`,
    padding: "14px 16px",
    boxSizing: "border-box",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.15)",
      zIndex: 1,
    },
  },
  cardTitle: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: dash.muted,
    marginBottom: 10,
    paddingBottom: 10,
    letterSpacing: "0.02em",
  },
}));

/** Display name aligned with RecentAccountsViewedTable / full preview rows */
function formatRecentAccountName(acct) {
  if (!acct) return "—";
  if (acct.firstName != null && String(acct.firstName).trim() !== "") {
    return `${acct.firstName}, ${acct.lastName || ""}`.trim();
  }
  return acct.lastName || "—";
}

function sortAccountsByVisitedOrder(accounts, visitedAccountNums) {
  const order = visitedAccountNums.map(String);
  return [...accounts].sort((a, b) => {
    const ia = order.indexOf(String(a.accountNum));
    const ib = order.indexOf(String(b.accountNum));
    return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
  });
}

function getVisitedAccountNumsFromStorage() {
  try {
    const u = JSON.parse(window.localStorage.getItem("user") || "null");
    return Array.isArray(u?.visitedAccounts) ? u.visitedAccounts : [];
  } catch {
    return [];
  }
}

/** Compact currency labels for chart Y-axis (full dollars). */
function formatAxisDollars(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "$0";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${Math.round(v)}`;
}

function CumulativeChart({ data, formatYTick }) {
  const { yTicks, yMax, band, points } = data;
  const fmt = formatYTick || ((t) => `$${t}K`);
  const showBand = band != null && typeof band.from === "number" && typeof band.to === "number";
  const svgW = 420;
  const svgH = 210;
  const padL = 44;
  const padR = 10;
  const padB = 30;
  const padT = 10;
  const innerW = svgW - padL - padR;
  const innerH = svgH - padT - padB;

  const progress = useEntranceProgress(
    [points.map((p) => p.value).join(","), yMax, points.length],
    680
  );

  const bars = useMemo(() => {
    const n = points.length;
    const slot = innerW / n;
    const barW = Math.max(8, slot * 0.38);
    return points.map((p, i) => {
      const x = padL + i * slot + (slot - barW) / 2;
      const hFull = (p.value / yMax) * innerH;
      return { ...p, x, w: barW, hFull, i };
    });
  }, [points, innerH, innerW, padL, yMax]);

  const bandY = showBand ? padT + innerH - (band.to / yMax) * innerH : 0;
  const bandH = showBand ? Math.max(1, ((band.to - band.from) / yMax) * innerH) : 0;

  return (
    <Box sx={{ position: "relative", width: "100%", height: svgH }}>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" height={svgH} preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="dashBarAccent" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={dash.accentLight} />
            <stop offset="100%" stopColor={dash.accentDark} />
          </linearGradient>
          <linearGradient id="dashBarGold" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={dash.chartHighlightLight} />
            <stop offset="100%" stopColor={dash.chartHighlightDark} />
          </linearGradient>
        </defs>
        {showBand ? <rect x={padL} y={bandY} width={innerW} height={bandH} fill={dash.chartBand} /> : null}
        {yTicks
          .filter((t) => t > 0)
          .map((t) => {
            const yy = padT + innerH - (t / yMax) * innerH;
            return (
              <g key={t}>
                <line x1={padL} x2={padL + innerW} y1={yy} y2={yy} stroke={dash.gridLine} strokeWidth="1" />
                <text x={padL - 6} y={yy + 4} textAnchor="end" fill={dash.muted} fontSize="9">
                  {fmt(t)}
                </text>
              </g>
            );
          })}
        <text x={6} y={padT + innerH + 3} fill={dash.muted} fontSize="10">
          {fmt(0)}
        </text>
        {bars.map((b) => {
          const h = b.hFull * progress;
          const y = padT + innerH - h;
          return (
            <rect
              key={b.i}
              x={b.x}
              y={y}
              width={b.w}
              height={h}
              rx={3}
              fill={b.hitTarget ? "url(#dashBarGold)" : "url(#dashBarAccent)"}
            />
          );
        })}
        {bars.map((b) => (
          <text
            key={`l-${b.i}`}
            x={b.x + b.w / 2}
            y={svgH - 6}
            textAnchor="middle"
            fill={dash.muted}
            fontSize="9"
          >
            {b.label}
          </text>
        ))}
      </svg>
    </Box>
  );
}

/** Counts up the past-due total when the value loads or changes (same easing as other entrances). */
function AnimatedPastDueAmount({ value }) {
  const progress = useEntranceProgress([value], 920);
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const display = safe * progress;
  return (
    <Typography sx={dashboardLargeCurrencyAmountSx}>
      {formatMoney(display)}
    </Typography>
  );
}

/** Bar height uses sqrt scale so large deltas stay readable; labels show exact amounts. */
function barFillPercent(value, maxVal) {
  const v = Math.max(Number(value) || 0, 0);
  const m = Math.max(Number(maxVal) || 0, 0);
  if (m <= 0 || v <= 0) return 0;
  return (Math.sqrt(v) / Math.sqrt(m)) * 100;
}

/** Keep move-in/out bars readable; footer row (count + label) is never clipped. */
const MOVE_INS_OUTS_BAR_TRACK_MIN_HEIGHT = 72;
const MOVE_INS_OUTS_BAR_TRACK_MAX_HEIGHT = 104;
const MOVE_INS_OUTS_BAR_MIN_WIDTH = 36;
const MOVE_INS_OUTS_COLUMN_MIN_WIDTH = 72;
const MOVE_INS_OUTS_FOOTER_MIN_HEIGHT = 42;
const MOVE_INS_OUTS_CHART_MIN_HEIGHT =
  MOVE_INS_OUTS_BAR_TRACK_MIN_HEIGHT + MOVE_INS_OUTS_FOOTER_MIN_HEIGHT;
const MOVE_INS_OUTS_CARD_MIN_HEIGHT = MOVE_INS_OUTS_CHART_MIN_HEIGHT + 96;

function workOrderDueDateKey(value) {
  if (value == null || value === "") return null;
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

function filterWorkOrdersDueTodayOrTomorrow(items) {
  const today = moment().format("YYYY-MM-DD");
  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
  return (items || [])
    .filter((wo) => {
      if (!wo?.workOrderTitle || String(wo.workOrderTitle).toLowerCase().includes("missing")) return false;
      if (wo.description === "Closed") return false;
      const key = workOrderDueDateKey(wo.dueDate);
      return key === today || key === tomorrow;
    })
    .sort((a, b) => {
      const aDue = workOrderDueDateKey(a.dueDate) || "";
      const bDue = workOrderDueDateKey(b.dueDate) || "";
      if (aDue !== bDue) return aDue.localeCompare(bDue);
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    })
    .slice(0, DASHBOARD_DUE_LIST_LIMIT);
}

function filterComplaintsDueTodayOrTomorrow(items) {
  const today = moment().format("YYYY-MM-DD");
  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
  const seen = new Set();
  return (items || [])
    .filter((c) => {
      if (!c || c.complaintStatus === "COMPLETED") return false;
      const key = complaintRowDueYmd(c);
      if (key !== today && key !== tomorrow) return false;
      const dedupeKey = c.id ?? c.uuid;
      if (dedupeKey != null) {
        if (seen.has(dedupeKey)) return false;
        seen.add(dedupeKey);
      }
      return true;
    })
    .sort((a, b) => {
      const aDue = complaintRowDueYmd(a) || "";
      const bDue = complaintRowDueYmd(b) || "";
      if (aDue !== bDue) return aDue.localeCompare(bDue);
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    })
    .slice(0, DASHBOARD_DUE_LIST_LIMIT);
}

function formatWorkOrderDueLabel(dueDate) {
  const key = workOrderDueDateKey(dueDate);
  if (!key) return "—";
  const today = moment().format("YYYY-MM-DD");
  const tomorrow = moment().add(1, "day").format("YYYY-MM-DD");
  if (key === today) return "Today";
  if (key === tomorrow) return "Tomorrow";
  return moment(dueDate).format("MMM D");
}

function formatWorkOrderSubtitle(wo) {
  const property = wo?.name ? String(wo.name).trim() : "";
  const address = formatWorkOrderAddress(wo);
  if (property && address) return `${property} · ${address}`;
  return property || address || wo?.description || "—";
}

function formatComplaintSubtitle(c) {
  const parts = [];
  if (c?.caseId) parts.push(String(c.caseId).trim());
  const customer = c?.accountName ? String(c.accountName).trim() : "";
  if (customer) parts.push(customer);
  else if (c?.accountNum != null) parts.push(`Account #${c.accountNum}`);
  return parts.join(" · ") || "—";
}

function formatComplaintDueLabel(c) {
  return formatWorkOrderDueLabel(complaintRowDueYmd(c));
}

/** Two vertical bars — move-ins vs move-outs for the selected period (/analytics). */
function MoveInsOutsBarColumn({ value, animatedFill, gradient, label }) {
  const count = Math.max(Number(value) || 0, 0);
  const fill = Math.min(Math.max(Number(animatedFill) || 0, 0), 100);

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: MOVE_INS_OUTS_COLUMN_MIN_WIDTH,
        display: "grid",
        gridTemplateRows: "minmax(0, 1fr) auto auto",
        gap: 0.5,
        alignItems: "center",
        justifyItems: "center",
        minHeight: MOVE_INS_OUTS_CHART_MIN_HEIGHT,
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: MOVE_INS_OUTS_BAR_TRACK_MIN_HEIGHT,
          maxHeight: MOVE_INS_OUTS_BAR_TRACK_MAX_HEIGHT,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "78%",
            minWidth: MOVE_INS_OUTS_BAR_MIN_WIDTH,
            maxWidth: 72,
            height: `${fill}%`,
            maxHeight: "100%",
            minHeight: count > 0 && fill > 0 ? 6 : 0,
            background: gradient,
            borderRadius: "4px 4px 0 0",
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 700,
          color: dash.text,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.2,
        }}
      >
        {numberWithCommas(count)}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.65rem",
          color: dash.muted,
          textAlign: "center",
          lineHeight: 1.2,
          pb: 0.25,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MoveInsOutsBars({ moveIns, moveOuts }) {
  const insN = Math.max(Number(moveIns) || 0, 0);
  const outsN = Math.max(Number(moveOuts) || 0, 0);
  const max = Math.max(insN, outsN, 1);
  const insPct = barFillPercent(insN, max);
  const outsPct = barFillPercent(outsN, max);
  const progress = useEntranceProgress([moveIns, moveOuts], 650);
  const insH = insPct * progress;
  const outsH = outsPct * progress;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: MOVE_INS_OUTS_CHART_MIN_HEIGHT,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          gap: 2,
          width: "100%",
          minHeight: MOVE_INS_OUTS_CHART_MIN_HEIGHT,
        }}
      >
        <MoveInsOutsBarColumn
          value={insN}
          animatedFill={insH}
          gradient={`linear-gradient(180deg, ${dash.accentLight}, ${dash.accentDark})`}
          label="Move-ins"
        />
        <MoveInsOutsBarColumn
          value={outsN}
          animatedFill={outsH}
          gradient={`linear-gradient(180deg, ${dash.chartHighlightLight}, ${dash.chartHighlightDark})`}
          label="Move-outs"
        />
      </Box>
    </Box>
  );
}

function DashboardPageInner(props) {
  const classes = useStyles();
  const location = useLocation();
  const { period, setPeriod, label: periodLabel, analyticsParams, startDisplay, endDisplay } = useDashboardPeriod();
  const [totalPastDue, setTotalPastDue] = useState(null);
  const [pastDueLoading, setPastDueLoading] = useState(true);
  const [pastDueError, setPastDueError] = useState(null);
  const [localAnalyticSummary, setLocalAnalyticSummary] = useState(null);
  const [localAnalyticsLoading, setLocalAnalyticsLoading] = useState(true);
  const [localAnalyticsError, setLocalAnalyticsError] = useState(null);
  const [recentAccountRows, setRecentAccountRows] = useState([]);
  const [recentAccountLoading, setRecentAccountLoading] = useState(
    () => getVisitedAccountNumsFromStorage().length > 0
  );
  const [recentAccountError, setRecentAccountError] = useState(null);
  const [billNewChargesByMonth, setBillNewChargesByMonth] = useState(null);
  const [billByMonthLoading, setBillByMonthLoading] = useState(true);
  const [billByMonthError, setBillByMonthError] = useState(null);
  const [dueItemsTab, setDueItemsTab] = useState(DASHBOARD_DUE_TAB_WORK_ORDERS);
  const [upcomingWorkOrders, setUpcomingWorkOrders] = useState([]);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(true);
  const [workOrdersError, setWorkOrdersError] = useState(null);
  const [upcomingComplaints, setUpcomingComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(true);
  const [complaintsError, setComplaintsError] = useState(null);

  useEffect(() => {
    document.title = "Ubil+ Overview Dashboard";
    props.getAllPropertyPreviews();
  }, [props.user]);

  useEffect(() => {
    let cancelled = false;
    let authPending = false;
    setLocalAnalyticsLoading(true);
    setLocalAnalyticsError(null);

    (async () => {
      try {
        const res = await fetchDashboardResource(() =>
          axios.get("/analytics", { params: analyticsParams })
        );
        if (!cancelled) setLocalAnalyticSummary(res.data);
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setLocalAnalyticsError(getDashboardErrorMessage(err, "Could not load analytics"));
          setLocalAnalyticSummary(null);
        }
      } finally {
        if (!cancelled && !authPending) setLocalAnalyticsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [period]);

  /** Same data as legacy Recent Accounts: POST /account/fullpreview/batch/acct-numbers + user.visitedAccounts */
  useEffect(() => {
    let cancelled = false;
    const userRaw = window.localStorage.getItem("user");
    let visited = [];
    try {
      const u = userRaw ? JSON.parse(userRaw) : null;
      visited = Array.isArray(u?.visitedAccounts) ? u.visitedAccounts : [];
    } catch {
      visited = [];
    }

    if (visited.length === 0) {
      setRecentAccountRows([]);
      setRecentAccountLoading(false);
      setRecentAccountError(null);
      return undefined;
    }

    setRecentAccountLoading(true);
    setRecentAccountError(null);

    (async () => {
      let authPending = false;
      try {
        const res = await fetchDashboardResource(() =>
          axios.post("/account/fullpreview/batch/acct-numbers", { accountNums: visited })
        );
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setRecentAccountRows(sortAccountsByVisitedOrder(list, visited));
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setRecentAccountError(getDashboardErrorMessage(err, "Could not load recent accounts"));
          setRecentAccountRows([]);
        }
      } finally {
        if (!cancelled && !authPending) setRecentAccountLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    let authPending = false;
    setPastDueLoading(true);
    setPastDueError(null);

    (async () => {
      try {
        const res = await fetchDashboardResource(() =>
          axios.get("/get-accounts-filtered/?page=0&pageLimit=0&status=Active")
        );
        if (cancelled) return;
        const raw = res.data?.summary?.combinedPastDue;
        const n = raw == null ? NaN : typeof raw === "number" ? raw : parseFloat(raw);
        setTotalPastDue(Number.isFinite(n) ? n : 0);
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setPastDueError(getDashboardErrorMessage(err, "Unable to load past due total"));
          setTotalPastDue(null);
        }
      } finally {
        if (!cancelled && !authPending) setPastDueLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let authPending = false;
    setBillByMonthLoading(true);
    setBillByMonthError(null);

    (async () => {
      try {
        const res = await fetchDashboardResource(() =>
          axios.get("/billing-new-charges-by-month", { params: { months: 6 } })
        );
        if (!cancelled) setBillNewChargesByMonth(res.data);
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setBillByMonthError(getDashboardErrorMessage(err, "Could not load billing totals by month"));
          setBillNewChargesByMonth(null);
        }
      } finally {
        if (!cancelled && !authPending) setBillByMonthLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let authPending = false;
    setWorkOrdersLoading(true);
    setWorkOrdersError(null);

    (async () => {
      try {
        const res = await fetchDashboardResource(() => axios.get("/workorders"));
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setUpcomingWorkOrders(filterWorkOrdersDueTodayOrTomorrow(list));
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setWorkOrdersError(getDashboardErrorMessage(err, "Could not load work orders"));
          setUpcomingWorkOrders([]);
        }
      } finally {
        if (!cancelled && !authPending) setWorkOrdersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let authPending = false;
    setComplaintsLoading(true);
    setComplaintsError(null);

    (async () => {
      try {
        const [overdueRes, warningRes] = await Promise.all([
          fetchDashboardResource(() =>
            axios.get("/complaints", { params: { sla: "overdue", limit: 50 } })
          ),
          fetchDashboardResource(() =>
            axios.get("/complaints", { params: { sla: "warning", limit: 50 } })
          ),
        ]);
        if (cancelled) return;
        const overdueItems = Array.isArray(overdueRes.data?.items) ? overdueRes.data.items : [];
        const warningItems = Array.isArray(warningRes.data?.items) ? warningRes.data.items : [];
        setUpcomingComplaints(filterComplaintsDueTodayOrTomorrow([...overdueItems, ...warningItems]));
      } catch (err) {
        if (!cancelled) {
          if (isDashboardAuthPending(err)) {
            authPending = true;
            return;
          }
          setComplaintsError(getDashboardErrorMessage(err, "Could not load complaints"));
          setUpcomingComplaints([]);
        }
      } finally {
        if (!cancelled && !authPending) setComplaintsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const billingSummary = localAnalyticSummary?.billingSummary?.summary;
  const paymentsSummary = localAnalyticSummary?.paymentsSummary?.summary;
  const totalBilled = Number(billingSummary?.totalBilled) || 0;
  const amountCollected = Number(paymentsSummary?.amountPaid) || 0;
  const analyticsLoading = localAnalyticsLoading;
  const accountsSummary = localAnalyticSummary?.accountsSummary?.summary;
  const moveInsTotal = Number(accountsSummary?.moveIns);
  const moveOutsTotal = Number(accountsSummary?.moveOuts);
  const moveInsPart = Number.isFinite(moveInsTotal) ? moveInsTotal : 0;
  const moveOutsPart = Number.isFinite(moveOutsTotal) ? moveOutsTotal : 0;

  const activeAccountsTotalRaw = Number(accountsSummary?.activeAccounts);
  const activeAccountsTotal = Number.isFinite(activeAccountsTotalRaw) ? activeAccountsTotalRaw : 0;

  const billNewChargesChartData = useMemo(() => {
    const items = billNewChargesByMonth?.items;
    if (!items || items.length === 0) return null;
    const points = items.map((row) => ({
      label: row.label,
      value: Number(row.newCharges) || 0,
      hitTarget: false,
    }));
    const maxVal = Math.max(...points.map((p) => p.value), 0);
    let yMax = maxVal <= 0 ? 1 : maxVal * 1.1;
    const pow = Math.pow(10, Math.floor(Math.log10(Math.max(yMax, 1))));
    yMax = Math.ceil(yMax / pow) * pow;
    const steps = 4;
    const yTicks = Array.from({ length: steps + 1 }, (_, i) => (yMax * i) / steps);
    return { points, yMax, yTicks, band: null };
  }, [billNewChargesByMonth]);

  const billChartRangeLabel = useMemo(() => {
    const items = billNewChargesByMonth?.items;
    if (!items || items.length === 0) return "Last 6 months";
    const first = items[0]?.label;
    const last = items[items.length - 1]?.label;
    return first && last ? `${first} – ${last}` : "Last 6 months";
  }, [billNewChargesByMonth]);

  return (
    <LeftSidebar>
      <div className={classes.root}>
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: dash.text }}>
              Overview
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: dash.muted, fontWeight: 400 }}>
              {startDisplay} – {endDisplay}
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={period}
            onChange={(e, next) => {
              if (next != null) setPeriod(next);
            }}
            aria-label="Dashboard date range"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                px: 1.5,
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              },
              "& .MuiToggleButton-root:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.12)",
              },
            }}
          >
            <ToggleButton value={DASHBOARD_PERIOD_LAST30}>Last 30 days</ToggleButton>
            <ToggleButton value={DASHBOARD_PERIOD_MTD}>Month to date</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            overflow: { xs: "auto", lg: "hidden" },
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              lg: "minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.9fr)",
            },
            gridTemplateRows: { lg: "minmax(0, 1fr)" },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          {/* —— Column 1 —— */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: 0,
              maxWidth: "100%",
              alignSelf: { xs: "stretch", lg: "start" },
            }}
          >
            <Box className={classes.card} sx={{ minWidth: 0, overflow: "hidden" }}>
              {pastDueLoading ? (
                <>
                  <Skeleton variant="text" width="70%" height={42} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="45%" height={20} />
                </>
              ) : pastDueError ? (
                <>
                  <Typography sx={{ fontSize: "0.875rem", color: dash.risk }}>{pastDueError}</Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: dash.muted, mt: 0.5 }}>Total past due balances</Typography>
                </>
              ) : (
                <>
                  <AnimatedPastDueAmount value={totalPastDue ?? 0} />
                  <Typography sx={{ fontSize: "0.8125rem", color: dash.muted, mt: 0.5 }}>
                    Total past due balances
                  </Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: dash.muted, mt: 1, lineHeight: 1.35 }}>
                    As of {moment().format("MMM D, YYYY")} · Active accounts
                  </Typography>
                </>
              )}
            </Box>

            <Box className={classes.card} sx={{ position: "relative", minWidth: 0, overflow: "hidden" }}>
              <Typography className={classes.cardTitle} sx={{ mb: 0.25 }}>
                Billed & Collected · {periodLabel}
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: dash.muted, mb: 1.25, lineHeight: 1.35 }}>
                {startDisplay} – {endDisplay}
              </Typography>
              {analyticsLoading ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  <Box sx={{ ...dashboardMetricStatBoxSx, p: 1.25 }}>
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="50%" height={18} sx={{ mt: 0.5 }} />
                  </Box>
                  <Box sx={{ ...dashboardMetricStatBoxSx, p: 1.25 }}>
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="50%" height={18} sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
              ) : localAnalyticsError ? (
                <Typography sx={{ fontSize: "0.875rem", color: dash.risk }}>{localAnalyticsError}</Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  <Box
                    sx={{
                      ...dashboardMetricStatBoxSx,
                      border: `1px solid ${dash.yellow}`,
                      borderRadius: 1.5,
                      p: 1.25,
                    }}
                  >
                    <Typography sx={dashboardCurrencyAmountSx}>{formatMoney(amountCollected)}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: dash.muted, mt: 0.5 }}>Total collected</Typography>
                  </Box>
                  <Box sx={{ ...dashboardMetricStatBoxSx, p: 1.25 }}>
                    <Typography sx={dashboardCurrencyAmountSx}>{formatMoney(totalBilled)}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: dash.muted, mt: 0.5 }}>Total billed</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <DisconnectPeriodMetricsCard
              periodLabel={periodLabel}
              period={period}
              cardClassName={classes.card}
              cardTitleClassName={classes.cardTitle}
            />
          </Box>

          {/* —— Column 2 —— Recent account activity (full column height) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              maxWidth: "100%",
              alignSelf: "stretch",
              minHeight: { xs: "auto", lg: 0 },
              height: { lg: "100%" },
              overflow: { xs: "visible", lg: "hidden" },
            }}
          >
            <Box
              className={classes.card}
              sx={{
                flex: { xs: "none", lg: 1 },
                display: "flex",
                flexDirection: "column",
                minHeight: { xs: "auto", lg: 0 },
                overflow: { xs: "visible", lg: "hidden" },
              }}
            >
              <Typography className={classes.cardTitle} sx={{ flexShrink: 0 }}>Recent account activity</Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, borderBottom: `1px solid ${dash.panelBorder}`, pb: 0.75, flexShrink: 0 }}>
                <Typography sx={{ fontSize: "0.7rem", color: dash.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Account
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: dash.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Balance
                </Typography>
              </Box>
              <Box sx={dashboardListScrollSx}>
                {recentAccountLoading ? (
                  <>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.65 }}>
                        <Skeleton variant="text" width="55%" height={20} />
                        <Skeleton variant="text" width="22%" height={20} />
                      </Box>
                    ))}
                  </>
                ) : recentAccountError ? (
                  <Typography sx={{ fontSize: "0.8125rem", color: dash.risk }}>{recentAccountError}</Typography>
                ) : recentAccountRows.length === 0 ? (
                  <Typography sx={{ fontSize: "0.8125rem", color: dash.muted }}>
                    No recent accounts yet. Open accounts from search to see them here.
                  </Typography>
                ) : (
                  recentAccountRows.map((row) => (
                    <Box
                      key={row.uuid || row.accountNum}
                      component={Link}
                      to={`/accountmanager/${row.accountNum}`}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 1,
                        py: 0.65,
                        px: 2.0,
                        textDecoration: "none",
                        color: "inherit",
                        borderRadius: 1,
                        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: dash.text }}>
                          {row.accountNum}
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: dash.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {formatRecentAccountName(row)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, flexShrink: 0 }}>
                        {formatMoney(Number(row.balance) || 0)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>

          {/* —— Column 3 —— */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: 0,
              maxWidth: "100%",
              minHeight: { xs: "auto", lg: 0 },
              height: { lg: "100%" },
              alignSelf: "stretch",
              overflow: { xs: "visible", lg: "hidden" },
            }}
          >
            <Box className={classes.card} sx={{ flexShrink: 0 }}>
              <Typography className={classes.cardTitle}>Bill total (New Charges) · {billChartRangeLabel}</Typography>
              {billByMonthLoading ? (
                <Skeleton variant="rounded" width="100%" height={210} sx={{ borderRadius: 1 }} />
              ) : billByMonthError ? (
                <Typography sx={{ fontSize: "0.875rem", color: dash.risk }}>{billByMonthError}</Typography>
              ) : billNewChargesChartData ? (
                <CumulativeChart data={billNewChargesChartData} formatYTick={formatAxisDollars} />
              ) : (
                <Typography sx={{ fontSize: "0.8125rem", color: dash.muted }}>No billing data for this range.</Typography>
              )}
            </Box>

            <Box
              sx={{
                flex: { xs: "none", lg: 1 },
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(220px, 1fr) minmax(260px, 1.4fr)",
                },
                gap: 1.5,
                minHeight: { xs: "auto", lg: 0 },
                overflow: { xs: "visible", lg: "hidden" },
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  minWidth: 0,
                  minHeight: "auto",
                }}
              >
                <Box
                  className={classes.card}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: MOVE_INS_OUTS_CARD_MIN_HEIGHT,
                  }}
                >
                  <Typography
                    className={classes.cardTitle}
                    sx={{ alignSelf: "flex-start", width: "100%", mb: 0.25, flexShrink: 0 }}
                  >
                    Move-Ins/Outs · {periodLabel}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: dash.muted,
                      mb: 0.5,
                      lineHeight: 1.35,
                      width: "100%",
                      flexShrink: 0,
                    }}
                  >
                    {startDisplay} – {endDisplay}
                  </Typography>
                  {analyticsLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        width: "100%",
                        mt: 0.5,
                        minHeight: MOVE_INS_OUTS_CHART_MIN_HEIGHT,
                        alignItems: "flex-end",
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        width="50%"
                        height={MOVE_INS_OUTS_BAR_TRACK_MAX_HEIGHT}
                        sx={{ borderRadius: 1, minWidth: MOVE_INS_OUTS_BAR_MIN_WIDTH, maxWidth: 72 }}
                      />
                      <Skeleton
                        variant="rounded"
                        width="50%"
                        height={MOVE_INS_OUTS_BAR_TRACK_MAX_HEIGHT}
                        sx={{ borderRadius: 1, minWidth: MOVE_INS_OUTS_BAR_MIN_WIDTH, maxWidth: 72 }}
                      />
                    </Box>
                  ) : localAnalyticsError ? (
                    <Typography sx={{ fontSize: "0.875rem", color: dash.risk, mt: 0.5, alignSelf: "stretch" }}>
                      {localAnalyticsError}
                    </Typography>
                  ) : (
                    <Box sx={{ mt: 0.5, flexShrink: 0 }}>
                      <MoveInsOutsBars moveIns={moveInsPart} moveOuts={moveOutsPart} />
                    </Box>
                  )}
                </Box>

                <Box
                  className={classes.card}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    py: 1.25,
                  }}
                >
                  {analyticsLoading ? (
                    <>
                      <Skeleton variant="text" width="55%" height={18} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width={72} height={36} />
                      <Skeleton variant="text" width="85%" height={18} sx={{ mt: 0.5 }} />
                    </>
                  ) : localAnalyticsError ? (
                    <Typography sx={{ fontSize: "0.8125rem", color: dash.risk }}>{localAnalyticsError}</Typography>
                  ) : (
                    <>
                      <Typography className={classes.cardTitle} sx={{ mb: 0.75, alignSelf: "flex-start", width: "100%" }}>
                        Total active accounts
                      </Typography>
                      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: 1 }}>
                        {numberWithCommas(activeAccountsTotal)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.65rem", color: dash.muted, mt: 0.5, lineHeight: 1.35 }}>
                        As of {moment().format("MMM D, YYYY")}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <Box
                className={classes.card}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignSelf: "stretch",
                  minHeight: { xs: 280, lg: 0 },
                  height: { lg: "100%" },
                  overflow: { xs: "visible", lg: "hidden" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                    pb: 1,
                    borderBottom: `1px solid ${dash.panelBorder}`,
                    flexShrink: 0,
                  }}
                >
                  <Typography className={classes.cardTitle} sx={{ mb: 0, pb: 0 }}>
                    Due today &amp; tomorrow
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={dueItemsTab}
                    onChange={(_e, next) => {
                      if (next != null) setDueItemsTab(next);
                    }}
                    aria-label="Due items view"
                    sx={dashboardDueTabToggleSx}
                  >
                    <ToggleButton value={DASHBOARD_DUE_TAB_WORK_ORDERS}>Work orders</ToggleButton>
                    <ToggleButton value={DASHBOARD_DUE_TAB_COMPLAINTS}>Complaints due soon</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, borderBottom: `1px solid ${dash.panelBorder}`, pb: 0.75, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: "0.7rem", color: dash.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {dueItemsTab === DASHBOARD_DUE_TAB_COMPLAINTS ? "Complaint" : "Work order"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: dash.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Due
                  </Typography>
                </Box>
                <Box sx={dashboardListScrollSx}>
                  {dueItemsTab === DASHBOARD_DUE_TAB_WORK_ORDERS ? (
                    workOrdersLoading ? (
                      <>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.65 }}>
                            <Skeleton variant="text" width="62%" height={20} />
                            <Skeleton variant="text" width="18%" height={20} />
                          </Box>
                        ))}
                      </>
                    ) : workOrdersError ? (
                      <Typography sx={{ fontSize: "0.8125rem", color: dash.risk }}>{workOrdersError}</Typography>
                    ) : upcomingWorkOrders.length === 0 ? (
                      <Typography sx={{ fontSize: "0.8125rem", color: dash.muted }}>
                        No work orders due today or tomorrow.
                      </Typography>
                    ) : (
                      upcomingWorkOrders.map((wo) => (
                        <Box
                          key={wo.uuid || wo.id}
                          component={Link}
                          to={`/workorders?id=${wo.id}`}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            gap: 1,
                            py: 0.65,
                            px: 0.5,
                            textDecoration: "none",
                            color: "inherit",
                            borderRadius: 1,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: dash.text }}>
                              WO #{wo.id} — {wo.workOrderTitle || "Untitled"}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: dash.muted,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatWorkOrderSubtitle(wo)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, flexShrink: 0, color: dash.text }}>
                            {formatWorkOrderDueLabel(wo.dueDate)}
                          </Typography>
                        </Box>
                      ))
                    )
                  ) : complaintsLoading ? (
                    <>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.65 }}>
                          <Skeleton variant="text" width="62%" height={20} />
                          <Skeleton variant="text" width="18%" height={20} />
                        </Box>
                      ))}
                    </>
                  ) : complaintsError ? (
                    <Typography sx={{ fontSize: "0.8125rem", color: dash.risk }}>{complaintsError}</Typography>
                  ) : upcomingComplaints.length === 0 ? (
                    <Typography sx={{ fontSize: "0.8125rem", color: dash.muted }}>
                      No complaints due today or tomorrow.
                    </Typography>
                  ) : (
                    upcomingComplaints.map((c) => (
                      <Box
                        key={c.uuid || c.id}
                        component={Link}
                        to="/complaints"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          gap: 1,
                          py: 0.65,
                          px: 0.5,
                          textDecoration: "none",
                          color: "inherit",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: dash.text }}>
                            #{c.id} — {c.complaintTitle || "Untitled"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: dash.muted,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatComplaintSubtitle(c)}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            flexShrink: 0,
                            color: c.slaState === "overdue" ? dash.risk : dash.text,
                          }}
                        >
                          {formatComplaintDueLabel(c)}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </LeftSidebar>
  );
};

MoveInsOutsBars.propTypes = {
  moveIns: PropTypes.number,
  moveOuts: PropTypes.number,
};

DashboardPageInner.propTypes = {
  user: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  getAllPropertyPreviews: PropTypes.func.isRequired,
};

function DashboardPageState(state) {
  return {
    user: state.user,
  };
}

function DashboardWithPeriodProvider(props) {
  return (
    <DashboardPeriodProvider>
      <DashboardPageInner {...props} />
    </DashboardPeriodProvider>
  );
}

export default connect(DashboardPageState, {
  logout,
  getAllPropertyPreviews,
})(DashboardWithPeriodProvider);
